import { lazy, Suspense, type ComponentType } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

import { AuthGate, RequireSystem } from '@/components/common/AuthGate'
import { LoadingState } from '@/components/common/States'
import { AppLayout } from '@/components/layout/AppLayout'
import { LoginPage } from '@/pages/LoginPage'
import { ChangePasswordPage } from '@/pages/ChangePasswordPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { SetPasswordPage } from '@/pages/SetPasswordPage'
import { HomePage } from '@/pages/HomePage'
import { ProjectsPage } from '@/pages/ProjectsPage'
import { ProjectIndexRedirect, ProjectLayout } from '@/pages/ProjectLayout'
import { NotesListPage } from '@/pages/NotesListPage'
import { MembersTab } from '@/pages/MembersTab'
import { ActivityTab } from '@/pages/ActivityTab'
import { ProjectSettingsTab } from '@/pages/ProjectSettingsTab'

/**
 * Ağır bağımlılık taşıyan ekranlar tembel yüklenir.
 *
 * <p>Hepsi tek pakete girdiğinde giriş ekranını açmak için bile 1,27 MB
 * JavaScript iniyordu: zengin metin editörü (tiptap/ProseMirror) yalnızca
 * not editörü ve paylaşım sayfasında, QR üretici yalnızca güvenlik
 * ekranında, sürükle-bırak yalnızca dosyalarda kullanılıyor. Kabuk, giriş
 * ve ilk açılan liste ekranları eager kalır: onlar her oturumda açılır ve
 * tembel yüklenmeleri yalnızca bir yükleme dönencesi eklerdi.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function tembel<M extends Record<K, ComponentType<any>>, K extends keyof M>(
  yukle: () => Promise<M>, ad: K,
) {
  // Sayfanın kendi özellik tipi korunur: <FileEditorPage mode="…"> hâlâ denetlenir.
  type Ozellikler = React.ComponentProps<M[K]>
  const Bilesen = lazy(() => yukle().then((m) => ({ default: m[ad] })))
  return function TembelSayfa(props: Ozellikler) {
    return (
      <Suspense fallback={<LoadingState />}>
        <Bilesen {...props} />
      </Suspense>
    )
  }
}

const NoteEditorPage = tembel(() => import('@/pages/NoteEditorPage'), 'NoteEditorPage')
const FilesPage = tembel(() => import('@/pages/FilesPage'), 'FilesPage')
const FileEditorPage = tembel(() => import('@/pages/FileEditorPage'), 'FileEditorPage')
const SearchPage = tembel(() => import('@/pages/SearchPage'), 'SearchPage')
const SecurityPage = tembel(() => import('@/pages/SecurityPage'), 'SecurityPage')
const SharedPage = tembel(() => import('@/pages/SharedPage'), 'SharedPage')
const VaultPage = tembel(() => import('@/pages/VaultPage'), 'VaultPage')
const AllTasksPage = tembel(() => import('@/pages/AllTasksPage'), 'AllTasksPage')
const OffersPage = tembel(() => import('@/pages/OffersPage'), 'OffersPage')
const OffersTab = tembel(() => import('@/pages/OffersTab'), 'OffersTab')
const TasksPage = tembel(() => import('@/pages/TasksPage'), 'TasksPage')
const ProjectOverviewPage = tembel(() => import('@/pages/ProjectOverviewPage'), 'ProjectOverviewPage')
const AdminUsersPage = tembel(() => import('@/pages/admin/AdminUsersPage'), 'AdminUsersPage')
const AdminAuditPage = tembel(() => import('@/pages/admin/AdminAuditPage'), 'AdminAuditPage')
const AdminTeamsPage = tembel(() => import('@/pages/admin/AdminTeamsPage'), 'AdminTeamsPage')
const AdminRolesPage = tembel(() => import('@/pages/admin/AdminRolesPage'), 'AdminRolesPage')
const AdminMailPage = tembel(() => import('@/pages/admin/AdminMailPage'), 'AdminMailPage')
const AdminProjectsPage = tembel(() => import('@/pages/admin/AdminProjectsPage'), 'AdminProjectsPage')
const TestsTab = tembel(() => import('@/pages/tests/TestsTab'), 'TestsTab')
const DeployPage = tembel(() => import('@/pages/deploy/DeployPage'), 'DeployPage')
const DeployJobPage = tembel(() => import('@/pages/deploy/JobEditorPage'), 'JobEditorPage')
const DeployRunPage = tembel(() => import('@/pages/deploy/RunPage'), 'RunPage')

/**
 * Rotalar Türkçe.
 *
 * <p>Dosya editörü bilerek AppShell'in DIŞINDA: OnlyOffice kendi menülerini
 * çizer ve dar bir alana sıkıştırıldığında kullanılamaz hale gelir.
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },

  // Parola bağlantıları: AuthGate'in DIŞINDA. Buraya gelen kişi parolasını bilmiyor
  // (davet edilmiş ya da unutmuş); içeride olsaydı giriş ekranına yönlenirdi.
  { path: '/parolami-unuttum', element: <ForgotPasswordPage /> },
  { path: '/parola-belirle', element: <SetPasswordPage /> },

  // Paylaşım linki: AuthGate'in DIŞINDA. Ziyaretçinin hesabı yoktur; içeride
  // olsaydı bağlantı giriş ekranına yönlenir ve link hiç çalışmazdı.
  { path: '/paylasim/:token', element: <SharedPage /> },

  {
    path: '/parola-degistir',
    element: (
      <AuthGate>
        <ChangePasswordPage />
      </AuthGate>
    ),
  },

  // Tam ekran belge editörü
  {
    path: '/projeler/:projectId/dosyalar/:fileId/duzenle',
    element: (
      <AuthGate>
        <FileEditorPage mode="edit" />
      </AuthGate>
    ),
  },
  {
    path: '/projeler/:projectId/dosyalar/:fileId/goruntule',
    element: (
      <AuthGate>
        <FileEditorPage mode="view" />
      </AuthGate>
    ),
  },

  {
    path: '/',
    element: (
      <AuthGate>
        <AppLayout />
      </AuthGate>
    ),
    children: [
      { index: true, element: <HomePage /> },
      { path: 'projeler', element: <ProjectsPage /> },
      { path: 'proje-durumlari', element: <ProjectOverviewPage /> },
      { path: 'gorevler', element: <AllTasksPage /> },
      { path: 'teklifler', element: <OffersPage /> },
      {
        path: 'projeler/:projectId',
        element: <ProjectLayout />,
        children: [
          { index: true, element: <ProjectIndexRedirect /> },
          { path: 'notlar', element: <NotesListPage /> },
          { path: 'notlar/:noteId', element: <NoteEditorPage /> },
          { path: 'dosyalar', element: <FilesPage /> },
          { path: 'gorevler', element: <TasksPage /> },
          { path: 'kasa', element: <VaultPage /> },
          { path: 'kasa/:entryId', element: <VaultPage /> },
          { path: 'deploy', element: <DeployPage /> },
          { path: 'deploy/is/:jobId', element: <DeployJobPage /> },
          { path: 'deploy/calistirma/:runId', element: <DeployRunPage /> },
          { path: 'teklifler', element: <OffersTab /> },
          { path: 'teklifler/:offerId', element: <OffersTab /> },
          { path: 'uyeler', element: <MembersTab /> },
          { path: 'hareketler', element: <ActivityTab /> },
          { path: 'testler', element: <TestsTab /> },
          { path: 'ayarlar', element: <ProjectSettingsTab /> },
        ],
      },
      { path: 'arama', element: <SearchPage /> },
      { path: 'guvenlik', element: <SecurityPage /> },
      { path: 'yonetim/kullanicilar', element: <RequireSystem permission="admin.users"><AdminUsersPage /></RequireSystem> },
      { path: 'yonetim/ekipler', element: <RequireSystem permission="admin.teams"><AdminTeamsPage /></RequireSystem> },
      { path: 'yonetim/projeler', element: <RequireSystem permission="admin.projects.members"><AdminProjectsPage /></RequireSystem> },
      { path: 'yonetim/roller', element: <RequireSystem permission="admin.roles"><AdminRolesPage /></RequireSystem> },
      { path: 'yonetim/e-posta', element: <RequireSystem permission="admin.mail"><AdminMailPage /></RequireSystem> },
      { path: 'yonetim/denetim', element: <RequireSystem permission="admin.audit"><AdminAuditPage /></RequireSystem> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
